
import React from "react";
import { Link } from "react-router-dom";
import { EmptyState, PortalCard, PortalLoading, StatusBadge, money, niceDate, useParentPortal } from "../ParentPortal/parentPortalUtils";

function ParentDashboard() {
  const { data, loading, reload } = useParentPortal("parent-portal/dashboard/");

  if (loading && !data) return <PortalLoading />;

  const parent = data?.parent || {};
  const school = data?.school || {};
  const children = data?.children || [];
  const announcements = data?.announcements || [];

  return (
    <div className="container-fluid py-4">
      <div className="card border-0 shadow-sm mb-3 overflow-hidden">
        <div className="card-body p-4" style={{ background: "linear-gradient(135deg, #eef8f1, #ffffff)" }}>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
            <div>
              <small className="text-uppercase fw-bold text-success">Parent Portal</small>
              <h3 className="fw-bold mb-1" style={{ color: "#125b2d" }}>Welcome, {parent.first_name || parent.full_name || "Parent"}</h3>
              <p className="text-muted mb-0">View your linked children, school notices, fees, reports and school life updates.</p>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <button className="btn btn-light border" onClick={reload}><i className="bi bi-arrow-clockwise me-2"></i>Refresh</button>
              <Link className="btn btn-success" to="/parent/profile"><i className="bi bi-person-circle me-2"></i>My Profile</Link>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-lg-3 col-md-6">
          <PortalCard title="Linked Children" icon="bi-people" to="/parent/children">
            <h3 className="fw-bold mb-1">{children.length}</h3>
            <p className="text-muted small mb-0">student{children.length === 1 ? "" : "s"} linked to your parent account.</p>
          </PortalCard>
        </div>
        <div className="col-lg-3 col-md-6">
          <PortalCard title="School Announcements" icon="bi-megaphone" to="/parent/announcements">
            <h3 className="fw-bold mb-1">{announcements.length}</h3>
            <p className="text-muted small mb-0">parent-facing notice{announcements.length === 1 ? "" : "s"} available.</p>
          </PortalCard>
        </div>
        <div className="col-lg-3 col-md-6">
          <PortalCard title="Parent Communication" icon="bi-chat-left-text" to="/parent/reports">
            <h3 className="fw-bold mb-1"><i className="bi bi-send-check"></i></h3>
            <p className="text-muted small mb-0">Send private reports to your child’s class teacher.</p>
          </PortalCard>
        </div>
        <div className="col-lg-3 col-md-6">
          <PortalCard title="Reports Available" icon="bi-file-earmark-medical">
            <h3 className="fw-bold mb-1">{children.reduce((sum, row) => sum + Number(row.results?.available_reports || 0), 0)}</h3>
            <p className="text-muted small mb-0">published downloadable report card(s).</p>
          </PortalCard>
        </div>
      </div>


      {school?.name && (
        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
              <div className="d-flex align-items-center gap-3">
                {school.logo_url && (
                  <img
                    src={school.logo_url}
                    alt={school.name}
                    style={{ width: 64, height: 64, objectFit: "contain" }}
                  />
                )}
                <div>
                  <h5 className="fw-bold mb-1" style={{ color: "#125b2d" }}>{school.name}</h5>
                  {school.motto && <div className="text-muted fst-italic small">{school.motto}</div>}
                  <div className="text-muted small">
                    {[school.address, school.city, school.province, school.country].filter(Boolean).join(", ")}
                  </div>
                  <div className="text-muted small">
                    {[school.phone, school.alternative_phone, school.email, school.website].filter(Boolean).join(" | ")}
                  </div>
                </div>
              </div>
              <div className="text-md-end">
                <small className="text-muted d-block">School Office</small>
                <span className="badge text-bg-light border">Contact & Support</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="row g-3">
        <div className="col-xl-8">
          <PortalCard title="My Children" icon="bi-mortarboard" to="/parent/children">
            {children.length === 0 ? (
              <EmptyState title="No children linked" message="Ask the school office to link your parent account to your child." />
            ) : (
              <div className="row g-3">
                {children.map((row) => {
                  const child = row.student || {};
                  const fee = row.fee_status || {};
                  return (
                    <div className="col-md-6" key={child.id}>
                      <div className="border rounded-3 p-3 h-100">
                        <div className="d-flex justify-content-between gap-2">
                          <div>
                            <h6 className="fw-bold mb-1">{child.full_name}</h6>
                            <small className="text-muted">{child.student_number || "No student number"} · {child.class_group?.name || "No class group"}</small>
                          </div>
                          <StatusBadge value={fee.status_label || "Fee status"} />
                        </div>
                        <div className="row g-2 mt-2">
                          <Mini label="Outstanding" value={money(fee.outstanding_balance)} />
                          <Mini label="Today" value={`${row.today_timetable_count || 0} lessons`} />
                          <Mini label="Reports" value={`${row.results?.available_reports || 0}`} />
                          <Mini label="Conduct" value={`${row.conduct?.open_incidents || 0} open`} />
                        </div>
                        <div className="d-flex flex-wrap gap-2 mt-3">
                          <Link className="btn btn-sm btn-success" to={`/parent/children/${child.id}`}>Open</Link>
                          <Link className="btn btn-sm btn-light border" to={`/parent/children/${child.id}/results`}>Reports</Link>
                          <Link className="btn btn-sm btn-light border" to={`/parent/children/${child.id}/finance`}>Fees</Link>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </PortalCard>
        </div>

        <div className="col-xl-4">
          <PortalCard title="Latest Announcements" icon="bi-megaphone" to="/parent/announcements">
            {announcements.length === 0 ? <EmptyState title="No announcements" message="Parent notices will appear here." /> : announcements.slice(0, 6).map((item) => (
              <div className="border-bottom py-2" key={item.id}>
                <div className="d-flex justify-content-between gap-2">
                  <div className="fw-semibold">{item.title}</div>
                  <StatusBadge value={item.priority} />
                </div>
                <small className="text-muted">{niceDate(item.created_at || item.sent_at || item.scheduled_for)}</small>
                <p className="small text-muted mb-0 mt-1">{item.message}</p>
              </div>
            ))}
          </PortalCard>
        </div>
      </div>
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div className="col-6">
      <div className="bg-light rounded-3 p-2">
        <small className="text-muted fw-semibold">{label}</small>
        <div className="fw-bold">{value}</div>
      </div>
    </div>
  );
}

export default ParentDashboard;
